import { Injectable } from "@angular/core";
import {
  ContainerOfRequest,
  CurrentContainerOfRequest,
} from "app/models/container.model";
import { ActiveMachineReference } from "app/models/machine-reference.model";
import { CookieService } from "ng2-cookies";
import { DeviceSocketInfo } from "app/models/device-socket-info.model";
import { DeviceMode } from "app/models/enums.model";
import { BehaviorSubject, Observable } from "rxjs";

const COOKIE_NAME_SCREEN_KEYBOARD: string = "screenKeyboard";
const COOKIE_NAME_MACHINE_ID: string = "MachineId";
const COOKIE_NAME_LOCAL_MACHINES: string = "LocalMachines";
const COOKIE_VERIFIED_OFS: string = "VerifiedOfs";
const COOKIE_NAME_OF_ID: string = "ofId";
const DONT_ASK_AGAIN: string = "DontAskAgain";
const COOKIE_LAST_PRODUCTION: string = "LastProduction";
const COOKIE_LAST_CONTAINER: string = "LastContainer";
const COOKIE_CURRENT_CONTAINER: string = "CurrentContainer";
const LOCAL_DEVICES_INFO: string = "DevicesInfo";
const PENDING_SERIAL_NUMBERS: string = "PendingSerialNumbers";
const LAST_LABEL_PRINTED: string = "LastLabelPrinted";
const DEVICE_MODE: string = "DeviceMode_";

@Injectable({
  providedIn: "root",
})
export class BaseAppService {
  public slaveMachine: Observable<boolean>;
  public slaveMachineSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  constructor(private cookieService: CookieService) {
    this.slaveMachine = this.slaveMachineSubject.asObservable();
  }

  //Session storage

  public saveFilter(key: string, value: any) {
    sessionStorage.setItem(key, value);
  }

  public getFilter(key: string): any {
    let item = sessionStorage.getItem(key);
    return item != "" ? item : null;
  }

  public clearFilter(key: string) {
    sessionStorage.removeItem(key);
  }

  //LocalStorage
  public getDevicesInfo(): Map<string, DeviceSocketInfo> {
    let compressedItem = localStorage.getItem(LOCAL_DEVICES_INFO);
    if (compressedItem == null || compressedItem == "") return new Map();
    return JSON.parse(compressedItem);
  }

  public setDevicesInfo(devices: Map<string, DeviceSocketInfo>): void {
    let compressedItem = JSON.stringify(devices);
    localStorage.setItem(LOCAL_DEVICES_INFO, compressedItem);
  }

  public deleteDevicesInfo(): void {
    localStorage.removeItem(LOCAL_DEVICES_INFO);
  }

  public getLastLabelPrinted(): string {
    return localStorage.getItem(LAST_LABEL_PRINTED) ?? "";
  }

  public setLastLabelPrinted(label: string) {
    localStorage.setItem(LAST_LABEL_PRINTED, label);
  }

  public deleteLastLabelPrint() {
    localStorage.removeItem(LAST_LABEL_PRINTED);
  }

  public getDeviceMode(sn: string): DeviceMode | null {
    let value = localStorage.getItem(DEVICE_MODE + sn);
    if (!value || isNaN(Number(value))) return null;
    return Number(value) as DeviceMode;
  }

  public setDeviceMode(sn: string, deviceMode: DeviceMode) {
    let value = JSON.stringify(deviceMode);
    localStorage.setItem(DEVICE_MODE + sn, value);
  }

  public deleteDeviceMode() {
    localStorage.removeItem(DEVICE_MODE);
  }

  //Cookies

  public getShowScreenKeyboard(): boolean {
    return this.cookieService.get(COOKIE_NAME_SCREEN_KEYBOARD) == "1";
  }

  public setMachineId(machineId: string) {
    this.cookieService.set(COOKIE_NAME_MACHINE_ID, machineId, 365);
  }

  public setOfId(ofId: string) {
    this.cookieService.set(COOKIE_NAME_OF_ID, ofId, 365);
  }

  public setLocalMachines(localMachines: string[]) {
    this.cookieService.set(
      COOKIE_NAME_LOCAL_MACHINES,
      JSON.stringify(localMachines),
      365
    );
  }

  public setVerifiedOfs(verifiedOfs) {
    this.cookieService.set(
      COOKIE_VERIFIED_OFS,
      JSON.stringify(verifiedOfs),
      365
    );
  }

  public setLastProduction(lastProduction: ActiveMachineReference): void {
    this.cookieService.set(
      COOKIE_LAST_PRODUCTION,
      JSON.stringify(lastProduction),
      365
    );
  }

  public setLastContainer(lastContainer: ContainerOfRequest): void {
    this.cookieService.set(
      COOKIE_LAST_CONTAINER,
      JSON.stringify(lastContainer),
      365
    );
  }

  public setCurrentContainer(
    currentContainer: CurrentContainerOfRequest
  ): void {
    const existingData = localStorage.getItem(COOKIE_CURRENT_CONTAINER);
    let containers: CurrentContainerOfRequest[] = [];

    if (existingData) {
      containers = JSON.parse(existingData) as CurrentContainerOfRequest[];
    }

    const index = containers.findIndex(
      (c) =>
        c.oFId === currentContainer.oFId &&
        c.machineId === currentContainer.machineId
    );

    if (index !== -1) {
      containers[index] = currentContainer;
    } else {
      containers.push(currentContainer);
    }

    localStorage.setItem(COOKIE_CURRENT_CONTAINER, JSON.stringify(containers));
  }

  public getDontAskAgain() {
    return this.cookieService.get(DONT_ASK_AGAIN) == "True";
  }

  public setDontAskAgain(dontAskAgain) {
    dontAskAgain && this.cookieService.set(DONT_ASK_AGAIN, "True", 365);
  }

  public setPendingSerialNumbers(pendingSerialNumbers: string[]): void {
    localStorage.setItem(
      PENDING_SERIAL_NUMBERS,
      JSON.stringify(pendingSerialNumbers)
    );
  }

  public addPendingSerialNumber(serialNumber: string): string[] {
    let pendingSerialNumbers = this.getPendingSerialNumbers();
    let index = pendingSerialNumbers.indexOf(serialNumber);
    if (index != -1) return pendingSerialNumbers;
    pendingSerialNumbers.push(serialNumber);
    this.setPendingSerialNumbers(pendingSerialNumbers);
    return pendingSerialNumbers;
  }

  public deletePendingSerialNumber(serialNumber: string): string[] {
    let pendingSerialNumbers = this.getPendingSerialNumbers();
    let index = pendingSerialNumbers.indexOf(serialNumber);
    if (index == -1) return pendingSerialNumbers;
    pendingSerialNumbers.splice(index, 1);
    this.setPendingSerialNumbers(pendingSerialNumbers);
    return pendingSerialNumbers;
  }

  public getPendingSerialNumbers(): string[] {
    let pendingSerialNumbers = localStorage.getItem(PENDING_SERIAL_NUMBERS);
    return pendingSerialNumbers && pendingSerialNumbers != ""
      ? JSON.parse(pendingSerialNumbers)
      : [];
  }

  public deleteDontAskAgain() {
    this.cookieService.delete(DONT_ASK_AGAIN);
  }

  public getMachineId(): string | null {
    let item = this.cookieService.get(COOKIE_NAME_MACHINE_ID);
    return item != "" ? item : null;
  }

  public getOfId(): string | null {
    let item = this.cookieService.get(COOKIE_NAME_OF_ID);
    return item != "" ? item : null;
  }

  public getLocalMachines(): string[] {
    let notParsed = this.cookieService.get(COOKIE_NAME_LOCAL_MACHINES);
    let item = notParsed != "" ? JSON.parse(notParsed) : [];
    return item;
  }

  public getVerifiedOfs(): Map<string, boolean> {
    let notParsed = this.cookieService.get(COOKIE_VERIFIED_OFS);
    let item = notParsed != "" ? JSON.parse(notParsed) : {};
    return item;
  }

  public getLastProduction(): ActiveMachineReference {
    let notParsed = this.cookieService.get(COOKIE_LAST_PRODUCTION);
    let item = notParsed != "" ? JSON.parse(notParsed) : null;
    return item;
  }

  public getLastContainer(): ContainerOfRequest {
    let notParsed = this.cookieService.get(COOKIE_LAST_CONTAINER);
    let item = notParsed != "" ? JSON.parse(notParsed) : null;
    return item;
  }

  public getCurrentContainer(
    ofId: string,
    machineId: string | null
  ): CurrentContainerOfRequest | undefined {
    const existingData = localStorage.getItem(COOKIE_CURRENT_CONTAINER);
    if (!existingData) {
      return undefined;
    }

    const containers: CurrentContainerOfRequest[] = JSON.parse(existingData);

    return containers.find(
      (container) =>
        container.oFId === ofId && container.machineId === machineId
    );
  }

  public deleteMachineId() {
    return this.cookieService.delete(COOKIE_NAME_MACHINE_ID);
  }

  public deleteOfId() {
    return this.cookieService.delete(COOKIE_NAME_OF_ID);
  }

  public deleteLocalMachines() {
    return this.cookieService.delete(COOKIE_NAME_LOCAL_MACHINES);
  }

  public deleteVerifiedOfs() {
    return this.cookieService.delete(COOKIE_VERIFIED_OFS);
  }

  public deleteLastProduction() {
    return this.cookieService.delete(COOKIE_LAST_PRODUCTION);
  }

  public deleteLastContainer() {
    return this.cookieService.delete(COOKIE_LAST_CONTAINER);
  }

  //Generic

  public clearAll() {
    this.deleteMachineId();
    this.deleteOfId();
    this.deleteLocalMachines();
    //Revisar perque no s'esborra el devicesInfo, probablement es torna a posar quan rebem un disconnect
    this.deleteDevicesInfo();
    this.deleteVerifiedOfs();
    //this.clearFilter("OnlyMachine");
    //this.clearFilter("ShowAll");
  }
}
